import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import useColors from "../../../../../api/hooks/useColors";
import { LATEST } from "../../../../../utils/constants";
import { baseTimePointOptions } from "../../utils/commonWidgetUtils";

const TeamRemediationChart = ({
  teamRemediationData,
  baseTimePoint,
  colorsChart,
  colorsChart48percent,
}) => {
  const colors = useColors();

  const apexSeries = useMemo(
    () => [
      {
        name:
          baseTimePoint === LATEST
            ? "Current"
            : baseTimePointOptions[baseTimePoint],
        data: teamRemediationData.map((member, index) => ({
          x: member.displayName,
          y: member.baseTotal,
          fillColor: colorsChart48percent[index % colorsChart48percent.length],
        })),
      },
      {
        name: "Current",
        data: teamRemediationData.map((member, index) => ({
          x: member.displayName,
          y: member.currentTotal,
          fillColor: colorsChart[index % colorsChart.length],
        })),
      },
    ],
    [teamRemediationData, baseTimePoint, colorsChart, colorsChart48percent]
  );

  const apexOptions = useMemo(() => {
    return {
      chart: {
        type: "bar",
        height: 350,
        toolbar: { show: false },
        fontFamily: "inherit",
        id: "team-remediation-performance",
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "32px",
          borderRadius: 4,
          borderRadiusApplication: "end",
          rangeBarOverlap: false,
        },
      },
      colors: apexSeries.flatMap((s) => s.data.map((d) => d.fillColor)),
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: function ({ dataPointIndex }) {
          const member = teamRemediationData[dataPointIndex];
          if (!member) return "";

          const current = member.currentTotal;
          const previous = member.baseTotal;
          const trend = member.percentageChange;
          const isPositive = member.isPositive;

          const arrow = isPositive ? "↑" : trend === 0 ? "" : "↓";
          const trendColor = isPositive
            ? colors.success_main
            : trend === 0
            ? colors.text_disabled
            : colors.error_main;
          const trendPrefix = isPositive ? "+" : "";

          return `
            <div style="
              font-family: inherit;
              text-align: left;
              transform: translateX(-50%);
              position: relative;
              left: 50%;
              background: white;
              padding: 12px;
              border-radius: 4px;
              box-shadow: 0 2px 6px rgba(0,0,0,0.1);
              min-width: 140px;
            ">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; gap: 4px;">
                <div style="font-size: 14px; color: ${
                  colors.text_primary
                }; font-weight: 500; font-family: poppins;">
                  ${member.displayName}
                </div>
                ${
                  baseTimePoint !== LATEST
                    ? `
                  <div style="font-size: 14px; color: ${trendColor}; margin-left: 8px;">
                    ${arrow}<span style="margin-left: 4px;">${trendPrefix}${trend}%</span>
                  </div>
                `
                    : ""
                }
              </div>
              <div style="font-size: 12px; color: ${
                colors.text_secondary
              }; font-weight: 500; font-family: poppins;">
                Gap severity
              </div>
              <div style="font-size: 12px; margin-bottom: 4px; display: flex; flex-direction: column; align-items: flex-start;">
               <div style="display: flex; flex-direction: row; align-items: center; justify-content: space-between; width: 100%;">
                  <div style="display: flex; flex-direction: row; align-items: center; gap: 4px;">
                    <span  style="font-size: 12px; background-color:${
                      colors.ops_heatmap_low
                    }; width: 8px; height: 8px; border-radius: 2px; box-shadow: 0 0 1px 0.5px ${
            colors.grey_24percent
          }"></span>
                      <span style="color: ${
                        colors.text_secondary
                      };">High:</span>
                  </div>
                  <span style="color: ${
                    colors.text_secondary
                  }; margin-left: 4px;">${member.high}</span>
                </div>
                <div style="display: flex; flex-direction: row; align-items: center; justify-content: space-between; width: 100%;">
                  <div style="display: flex; flex-direction: row; align-items: center; gap: 4px;">
                    <span  style="font-size: 12px; background-color:${
                      colors.ops_heatmap_medium
                    }; width: 8px; height: 8px; border-radius: 2px; box-shadow: 0 0 1px 0.5px ${
            colors.grey_24percent
          }"></span>
                    <span style="color: ${
                      colors.text_secondary
                    };">Medium:</span>
                  </div>
                  <span style="color: ${
                    colors.text_secondary
                  }; margin-left: 4px;">${member.medium}</span>
                </div>
                <div style="display: flex; flex-direction: row; align-items: center; justify-content: space-between; width: 100%;">
                  <div style="display: flex; flex-direction: row; align-items: center; gap: 4px;">
                  <span  style="font-size: 12px; background-color:${
                    colors.ops_heatmap_green_fill
                  }; width: 8px; height: 8px; border-radius: 2px; box-shadow: 0 0 1px 0.5px ${
            colors.grey_24percent
          }"></span>
                  <span style="color: ${colors.text_secondary};">Low:</span>
                  </div>
                  <span style="color: ${
                    colors.text_secondary
                  }; margin-left: 4px;">${member.low}</span>
                </div>
              </div>
              <div style="background-color: ${
                colors.divider
              }; height: 1px; margin-top: 4px; width: 100%;"></div>
            
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: space-between; width: 100%;">
              <div style="font-size: 12px; color: ${
                colors.text_secondary
              }; font-weight: 400; font-family: poppins;">
                Total
              </div>
              <div style="font-size: 12px; color: ${
                colors.text_secondary
              }; font-weight: 400; font-family: poppins;">
                ${member.currentTotal}
              </div>
              </div>
              ${
                baseTimePoint !== LATEST
                  ? `
                <div style="font-size: 12px; display: flex; flex-direction: row; align-items: center; justify-content: space-between; width: 100%;">
                  <span style="color: ${colors.text_secondary};">Previous:</span>
                  <span style="color: ${colors.text_secondary}; margin-left: 4px;">${previous}</span>
                </div>
              `
                  : ""
              }
            </div>
          `;
        },
      },
      grid: {
        strokeDashArray: 4,
        borderColor: "rgba(145, 158, 171, 0.2)",
      },
      xaxis: {
        categories: teamRemediationData.map((member) => member.displayName),
        labels: {
          style: {
            colors: colors.text_disabled,
            fontSize: "10px",
          },
        },
      },
      yaxis: {
        min: 0,
        labels: {
          style: {
            colors: colors.text_disabled,
            fontSize: "10px",
          },
          formatter: (value) => Math.round(value),
        },
      },
      legend: {
        show: false,
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
    };
  }, [
    teamRemediationData,
    baseTimePoint,
    apexSeries,
    colors.error_main,
    colors.success_main,
    colors.text_disabled,
  ]);

  if (!teamRemediationData || teamRemediationData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={200}
      >
        <Typography variant="body2" color="text.secondary">
          No team remediation data available
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="caption" marginLeft={2.5}>
        Entities affected by remediation efforts per team member{" "}
        {baseTimePoint !== LATEST
          ? `vs. ${baseTimePointOptions[baseTimePoint]}`
          : ""}
      </Typography>

      <Box
        sx={{
          height: "350px",
          width: "100%",
          mt: 2,
          position: "relative",
        }}
      >
        <ReactApexChart
          key={`team-remediation-performance-chart-${teamRemediationData.length}-${baseTimePoint}`}
          options={apexOptions}
          series={baseTimePoint === LATEST ? [apexSeries[1]] : apexSeries}
          type="bar"
          height={350}
          yAxis={[
            {
              min: 0,
              tickLabelStyle: {
                fontSize: "10px",
              },
              valueFormatter: (value) => Math.round(value),
            },
          ]}
          margin={{
            top: 20,
            right: 20,
            bottom: 40,
            left: 40,
          }}
          sx={{
            "& .MuiChartsAxis-root .MuiChartsAxis-tickLabel": {
              fill: colors.text_secondary,
            },
          }}
        />

        {/* Custom Trend Labels Overlay */}
        {baseTimePoint !== LATEST && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "65px",
              right: 0,
              bottom: 0,
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            {teamRemediationData.map((member, index) => {
              // Calculate position for each label
              const chartAreaWidth = 100; // Percentage width of chart area
              const memberCount = teamRemediationData.length;
              const barGroupWidth = chartAreaWidth / memberCount;
              const leftPosition = index * barGroupWidth + barGroupWidth / 2;

              // Calculate vertical position based on bar height
              const maxHeight = 350 - 80; // Chart height minus margins (40px top + 40px bottom)
              const maxValue = Math.max(
                ...teamRemediationData.map((m) => m.currentTotal)
              );
              const barHeight = (member.currentTotal / maxValue) * maxHeight;
              const topPosition = 30 + (maxHeight - barHeight) - 20; // 40px margin + calculated position - 20px above bar

              const trend = member.percentageChange;
              const trendText =
                trend === 0 ? "-" : `${member.isPositive ? "+" : "-"}${trend}%`;

              return (
                <Box
                  key={member.assigneeEmail + index}
                  sx={{
                    position: "absolute",
                    left: `${leftPosition}%`,
                    top: `${topPosition}px`,
                    transform: "translateX(-50%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "12px",
                      color: colors.text_primary,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {trendText}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </>
  );
};

export default TeamRemediationChart;
